import React from 'react';
import loadable from '@loadable/component';

import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { camelize } from '../../util/string';
import { propTypes } from '../../util/types';
import { useIntl } from '../../util/reactIntl';

import FallbackPage from './FallbackPage';
import { ASSET_NAME } from './LandingPage.duck';
import { decryptString } from '../../util/securityHelpers';

const PageBuilder = loadable(() =>
  import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
);

const WORKFLOW_COPY_MESSAGE_IDS = {
  title: 'LandingPage.workflow.title',
  description: 'LandingPage.workflow.description',
  items: [
    {
      title: 'LandingPage.workflow.search.title',
      description: 'LandingPage.workflow.search.description',
    },
    {
      title: 'LandingPage.workflow.book.title',
      description: 'LandingPage.workflow.book.description',
    },
    {
      title: 'LandingPage.workflow.create.title',
      description: 'LandingPage.workflow.create.description',
    },
  ],
};

const normalizeLocale = locale => (locale || '').toLowerCase();

const withContent = (field, content, fallbackFieldType) => {
  const fieldType = field?.fieldType || fallbackFieldType;
  return { ...field, fieldType, content };
};

const isEnglishWorkflowSection = section => {
  if (!section || section.sectionType !== 'columns') {
    return false;
  }
  const title = (section.title?.content || '').trim().toLowerCase();
  const description = (section.description?.content || '').trim().toLowerCase();
  const blockTitles = (section.blocks || []).map(block =>
    (block?.title?.content || '').trim().toLowerCase()
  );
  const hasEnglishTitle = title === 'the modern workflow for location booking';
  const hasEnglishBlocks =
    blockTitles.includes('search') &&
    blockTitles.includes('book') &&
    blockTitles.includes('create');
  const mentionsSceneAccess = description.includes('sceneaccess');
  return hasEnglishTitle || hasEnglishBlocks || mentionsSceneAccess;
};

const getWorkflowCopy = intl => ({
  title: intl.formatMessage({ id: WORKFLOW_COPY_MESSAGE_IDS.title }),
  description: intl.formatMessage({ id: WORKFLOW_COPY_MESSAGE_IDS.description }),
  items: WORKFLOW_COPY_MESSAGE_IDS.items.map(item => ({
    title: intl.formatMessage({ id: item.title }),
    description: intl.formatMessage({ id: item.description }),
  })),
});

const localizeWorkflowSection = (section, locale, intl) => {
  const normalizedLocale = normalizeLocale(locale);
  const workflowCopy = normalizedLocale.startsWith('th') ? getWorkflowCopy(intl) : null;

  if (!workflowCopy || !isEnglishWorkflowSection(section)) {
    return section;
  }

  const updatedBlocks = (section.blocks || []).map((block, index) => {
    const item = workflowCopy.items[index];
    if (!item) {
      return block;
    }
    return {
      ...block,
      title: withContent(block.title, item.title, 'heading3'),
      text: withContent(block.text, item.description, block.text?.fieldType || 'paragraph'),
    };
  });

  return {
    ...section,
    title: withContent(section.title, workflowCopy.title, 'heading2'),
    description: withContent(section.description, workflowCopy.description, 'paragraph'),
    blocks: updatedBlocks,
  };
};

const localizeLandingPageData = (pageData, locale, intl) => {
  if (!pageData || !Array.isArray(pageData.sections)) {
    return pageData;
  }

  const sections = pageData.sections.map(section => localizeWorkflowSection(section, locale, intl));
  return { ...pageData, sections };
};

export const LandingPageComponent = props => {
  const { pageAssetsData, pageLocaleVariantAvailability, inProgress, error } = props;

  const intl = useIntl();
  const locale = intl?.locale || 'en';
  const rawPageData = pageAssetsData?.[camelize(ASSET_NAME)]?.data;

  const cleanedPageData = localizeLandingPageData(rawPageData, locale, intl);

  return (
    <PageBuilder
      pageAssetsData={cleanedPageData}
      inProgress={inProgress}
      error={error}
      fallbackPage={<FallbackPage error={error} />}
      isLandingPage
      hasLocalizedVariants={pageLocaleVariantAvailability?.[ASSET_NAME] === true}
    />
  );
};

LandingPageComponent.propTypes = {
  pageAssetsData: object,
  inProgress: bool,
  error: propTypes.error,
};

const mapStateToProps = state => {
  const { pageAssetsData, pageLocaleVariantAvailability, inProgress, error } =
    state.hostedAssets || {};
  return { pageAssetsData, pageLocaleVariantAvailability, inProgress, error };
};

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const LandingPage = compose(connect(mapStateToProps))(LandingPageComponent);

export default LandingPage;
